/**
 * ChaserBot - Easy mode AI opponent.
 * Simple ball-chasing behavior with no zone restrictions.
 * Free roam across the entire field.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { BotBase } from "./BotBase";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";

/**
 * Easy mode chaser bot - simple chase-ball-shoot behavior
 * No zone restrictions, free roam across entire field
 */
export class ChaserBot extends BotBase {
  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player
  ) {
    super(
      scene,
      ball,
      AIRole.CHASER,
      homePosition,
      allowedZone,
      separationConfig,
      shadowGenerator,
      player,
      "chaserBot"
    );
  }

  /**
   * Blue jersey color for chaser bot
   */
  protected _getJerseyColor(): BABYLON.Color3 {
    return new BABYLON.Color3(0.05, 0.05, 0.8); // Blue jersey
  }

  /**
   * Simple decision making for Easy mode
   * Just chase ball, dribble, and shoot - no complex tactics
   */
  protected _makeDecision(
    distToBall: number,
    distToGoal: number,
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3
  ): void {
    // Simple priority: tackle if player has ball, otherwise chase
    if (this._player && this._player.hasBallPossession && distToBall < 2.5 && this._tackleCooldown <= 0) {
      this._state = AIState.TACKLE;
      return;
    }

    // If we have the ball and are close to goal, shoot
    if (this.hasBallPossession && distToGoal < 15 && Math.abs(myPos.x) < 4) {
      this._state = AIState.SHOOT;
      return;
    }

    // If we have the ball, dribble toward goal
    if (this.hasBallPossession) {
      this._state = AIState.DRIBBLE;
      return;
    }

    // Otherwise chase the ball
    this._state = AIState.CHASE_BALL;
  }
}